import { requestApi2 } from "@utils/http"
import { endpoints } from "api"
import { useEffect, useMemo, useState } from "react"
import { Bar, Line } from "react-chartjs-2"
import {
	ChartData,
	User,
	UserAction,
	aggregateBrowserStats,
	formatTimeSpent,
	generateCSV,
	getBrowser,
	getSortIndicator,
	moduleNameFromActionKey,
	summarizeTopModules,
	timeAgo,
	totalVisitsForUser,
} from "./UserTrackingUtils"

import {
	ActiveElement,
	ArcElement,
	BarElement,
	CategoryScale,
	ChartEvent,
	Chart as ChartJS,
	Legend,
	LinearScale,
	LineElement,
	PointElement,
	Plugin,
	Title,
	Tooltip,
} from "chart.js"

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	LineElement,
	PointElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
)

const USER_TIME_CHART_TOP_N_CHOICES = [10, 15, 25, 35, 50, 75, 100, 150, 200] as const
const DEFAULT_USER_TIME_CHART_TOP_N = 35
const MODULE_CHART_TOP_N = 12

function getLast30DaysRange() {
	const today = new Date()
	const from = new Date()
	from.setDate(today.getDate() - 29)
	return {
		from: toIsoDate(from),
		to: toIsoDate(today),
	}
}

function toIsoDate(date: Date): string {
	return date.toISOString().split("T")[0]!
}

function isWeekendIsoDate(date: string): boolean {
	const [year, month, day] = date.split("-").map(Number)
	if (year == null || month == null || day == null) return false
	const weekday = new Date(year, month - 1, day).getDay()
	return weekday === 0 || weekday === 6
}

const weekendShadingPlugin: Plugin<"line"> = {
	id: "weekendShading",
	beforeDatasetsDraw(chart) {
		const xScale = chart.scales.x
		if (xScale == null) return

		const { ctx, chartArea } = chart
		const labels = chart.data.labels ?? []
		ctx.save()
		ctx.fillStyle = "rgba(245, 158, 11, 0.12)"
		labels.forEach((label, index) => {
			if (typeof label !== "string" || !isWeekendIsoDate(label)) return

			const center = xScale.getPixelForValue(index)
			const left =
				index === 0
					? chartArea.left
					: (xScale.getPixelForValue(index - 1) + center) / 2
			const right =
				index === labels.length - 1
					? chartArea.right
					: (center + xScale.getPixelForValue(index + 1)) / 2

			ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top)
		})
		ctx.restore()
	},
}

const utInputDate =
	"rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm transition-colors hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
const utBtnPreset =
	"rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98]"
const utBtnPrimary =
	"rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
const utBtnToggleOn =
	"rounded-lg border border-slate-800 bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
const utBtnToggleOff =
	"rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
const utChartTitle = "mb-3 text-center text-lg font-semibold tracking-tight text-slate-800"
const utChartPanel = "mb-8 h-100 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
const utSectionLabel = "mb-3 text-center text-sm font-semibold text-slate-700"
const utCheckboxGrid =
	"grid w-full max-w-3xl grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-4 gap-y-2"
const utCheckboxRow = "flex items-center gap-2 rounded-md py-1 text-sm text-slate-700"
const thCls =
	"truncate border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
const thSortCls = `${thCls} cursor-pointer select-none hover:text-slate-700`
const tdCls = "truncate border-b border-slate-200 px-3 py-3 text-sm text-slate-700"

function AsciiVessel() {
	return (
		<pre className="relative flex justify-center text-xl whitespace-pre text-amber-800">
			{`
                                                        _____     
                                                    )___(
                                            _______/__/_
                                    ___     /===========|   ___
                    ____       __   [\\\]___/____________|__[///]   __
                    \\   \_____[\\]__/___________________________\__[//]___
                    \\                          DIPAI                     |
                    \\                     usertracking vessel             /
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      `}
		</pre>
	)
}

const BROWSER_COLORS: Record<string, string> = {
	chrome: "border-yellow-200 bg-yellow-50 text-yellow-700",
	firefox: "border-orange-200 bg-orange-50 text-orange-700",
	safari: "border-blue-200 bg-blue-50 text-blue-700",
	edge: "border-indigo-200 bg-indigo-50 text-indigo-700",
	opera: "border-red-200 bg-red-50 text-red-700",
	other: "border-slate-200 bg-slate-50 text-slate-500",
}

function BrowserBadge({ browser }: { browser: string }) {
	const cls = BROWSER_COLORS[browser] ?? BROWSER_COLORS.other
	return (
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
		>
			{browser}
		</span>
	)
}

type ActivityMetrics = {
	dau: number
	daily: { date: Date; activeUsers: number }[]
	mau: number
	ratio: number
}

export function UserTracking() {
	const [users, setUsers] = useState<User[]>([])
	const [companies, setCompanies] = useState<string[]>([])
	const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])
	const [showCompanyFilter, setShowCompanyFilter] = useState(false)

	const [selectedUser, setSelectedUser] = useState<User | null>(null)

	const [selectedUserActions, setSelectedUserActions] = useState<Record<
		string,
		UserAction
	> | null>(null)

	const [userActions, setUserActions] = useState<Record<number, Record<string, UserAction>>>({})
	const [filterActive, setFilterActive] = useState(true)
	const [sortConfig, setSortConfig] = useState<{
		key: string
		direction: "asc" | "desc"
	}>({
		key: "totalTimeSpent",
		direction: "desc",
	})

	const [showDailyActiveUsersChart, setShowDailyActiveUsersChart] = useState(true)
	const [showCompanyEngagementChart, setShowCompanyEngagementChart] = useState(true)
	const [showModuleReachChart, setShowModuleReachChart] = useState(true)
	const [showModuleTimeChart, setShowModuleTimeChart] = useState(true)
	const [showModuleVisitsChart, setShowModuleVisitsChart] = useState(true)
	const [showTopModuleUsersChart, setShowTopModuleUsersChart] = useState(true)
	const [showTotalTimeSpentByUsersChart, setShowTotalTimeSpentByUsersChart] = useState(true)
	const [selectedVisitModule, setSelectedVisitModule] = useState("")
	const [userTimeChartTopN, setUserTimeChartTopN] = useState<number | "all">(
		DEFAULT_USER_TIME_CHART_TOP_N
	)

	const [showChartFilter, setShowChartFilter] = useState(false)

	const [showAsciiVessel, setShowAsciiVessel] = useState(false)

	const [metrics, setMetrics] = useState<ActivityMetrics>()

	const [timeRange, setTimeRange] = useState(getLast30DaysRange)
	const handleDateRangeSelect = (range: "today" | "thisMonth" | "last30Days") => {
		const today = new Date()
		const from = new Date()

		switch (range) {
			case "today":
				setTimeRange({
					from: toIsoDate(today),
					to: toIsoDate(today),
				})
				break
			case "thisMonth":
				from.setDate(1)
				setTimeRange({
					from: toIsoDate(from),
					to: toIsoDate(today),
				})
				break
			case "last30Days":
				from.setDate(today.getDate() - 29)
				setTimeRange({
					from: toIsoDate(from),
					to: toIsoDate(today),
				})
				break
		}
	}

	const handleDateChange = (field: "from" | "to", value: string) => {
		setTimeRange((prev) => {
			const newRange = { ...prev, [field]: value }

			if (field === "to" && value < prev.from!) {
				newRange.to = prev.from
			}
			if (field === "from" && value > prev.to!) {
				newRange.from = prev.to
			}

			return newRange
		})
	}

	const fetchMetricsWithParams = async () => {
		const params: { timeRange: { from: Date; to: Date } } = {
			timeRange: {
				from: new Date(timeRange.from!),
				to: new Date(timeRange.to!),
			},
		}
		const response = await requestApi2(endpoints.userTrackingGetActivityMetrics, params)
		if (response != null) {
			setMetrics(response)
		}
	}

	useEffect(() => {
		fetchMetricsWithParams()
	}, [timeRange.from, timeRange.to])

	const handleDownloadCSV = () => {
		const csvData = generateCSV(filteredUsers, userActions)

		const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
		const link = document.createElement("a")
		const url = URL.createObjectURL(blob)
		link.setAttribute("href", url)
		link.setAttribute("download", "user_data.csv")
		link.style.visibility = "hidden"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url) // Free up memory
	}

	const fetchData = async () => {
		const usersResponse = await requestApi2(endpoints.userTrackingGetUsers)

		if (usersResponse == null) {
			return
		}

		const users = usersResponse.users.map((user: User) => ({
			id: user.id,
			name: user.name,
			email: user.email,
			companyName: user.companyName,
			userAgent: user.userAgent,
		}))
		setUsers(users)

		const uniqueCompanies = Array.from(new Set(users.map((user) => user.companyName)))
		setCompanies(uniqueCompanies)

		const allUserActions = await requestApi2(endpoints.userTrackingGetAllUserActionCounts, {
			timeRange: {
				from: new Date(timeRange.from!),
				to: new Date(timeRange.to!),
			},
		})

		if (allUserActions != null) {
			const userActionsMap = (
				allUserActions as Array<{
					userId: number
					actionCounts: Record<
						string,
						{
							count: number
							lastTimestamp: Date
							totalTimeSpent?: number
							component?: string
						}
					>
				}>
			).reduce(
				(acc, { userId, actionCounts }) => {
					acc[userId] = actionCounts
					return acc
				},
				{} as Record<number, Record<string, UserAction>>
			)

			setUserActions(userActionsMap)
		}
	}

	useEffect(() => {
		fetchData()
	}, [timeRange.from, timeRange.to])

	const handleCompanySelect = (company: string) => {
		setSelectedCompanies((prevSelected) => {
			if (prevSelected.includes(company)) {
				return prevSelected.filter((c) => c !== company)
			} else {
				return [...prevSelected, company]
			}
		})
	}

	const handleSelectAllCompanies = () => {
		if (selectedCompanies.length === companies.length) {
			setSelectedCompanies([])
		} else {
			setSelectedCompanies(companies)
		}
	}

	const handleUserSelect = async (user: User) => {
		const userActionsResponse = await requestApi2(endpoints.userTrackingGetUserActionCounts, {
			userId: user.id,
			timeRange: {
				from: new Date(timeRange.from!),
				to: new Date(timeRange.to!),
			},
		})
		if (userActionsResponse?.actionCounts != null) {
			setSelectedUser(user)
			setSelectedUserActions(userActionsResponse.actionCounts)
		} else {
			setSelectedUserActions(null)
		}
	}

	useEffect(() => {
		let cancelled = false
		const fetchSelectedUserActions = async () => {
			if (selectedUser == null) return
			const userActionsResponse = await requestApi2(
				endpoints.userTrackingGetUserActionCounts,
				{
					userId: selectedUser.id,
					timeRange: {
						from: new Date(timeRange.from!),
						to: new Date(timeRange.to!),
					},
				}
			)
			if (cancelled) return
			if (userActionsResponse?.actionCounts != null) {
				setSelectedUserActions(userActionsResponse.actionCounts)
			} else {
				setSelectedUserActions(null)
			}
		}

		if (selectedUser != null) {
			void fetchSelectedUserActions()
		}
		return () => {
			cancelled = true
		}
	}, [selectedUser, timeRange.from, timeRange.to])

	const handleSort = (key: string) => {
		let direction: "asc" | "desc" = "asc"
		if (sortConfig != null && sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc"
		}
		setSortConfig({ key, direction })
	}

	const filteredUsers = useMemo(() => {
		return users.filter((user) => {
			const company = user.companyName
			const companyMatch =
				selectedCompanies.length === 0 || selectedCompanies.includes(company)
			if (!companyMatch) return false

			if (filterActive) {
				const userAction = userActions[user.id]
				return userAction != null && Object.keys(userAction).length > 0
			}
			return true
		})
	}, [users, filterActive, selectedCompanies, userActions])

	const sortedUsers = useMemo(() => {
		return [...filteredUsers].sort((a, b) => {
			if (sortConfig?.key === "totalTimeSpent") {
				const totalTimeA = Object.values(userActions[a.id] ?? {}).reduce(
					(total, action) => total + (action.totalTimeSpent ?? 0),
					0
				)
				const totalTimeB = Object.values(userActions[b.id] ?? {}).reduce(
					(total, action) => total + (action.totalTimeSpent ?? 0),
					0
				)
				return sortConfig.direction === "asc"
					? totalTimeA - totalTimeB
					: totalTimeB - totalTimeA
			}

			return 0 // Default case, no sorting
		})
	}, [filteredUsers, sortConfig, userActions])

	const dailyActiveUsersChartData = useMemo<ChartData | null>(() => {
		if (metrics == null || metrics.daily.length === 0) return null
		return {
			labels: metrics.daily.map((row) => toIsoDate(row.date)),
			datasets: [
				{
					label: "Active users",
					data: metrics.daily.map((row) => row.activeUsers),
					backgroundColor: "rgba(14, 165, 233, 0.18)",
					borderColor: "rgba(14, 165, 233, 1)",
					borderWidth: 2,
				},
			],
		}
	}, [metrics])

	const summaryStats = useMemo(() => {
		let usersWithTime = 0
		let totalHours = 0
		let totalVisits = 0

		for (const user of filteredUsers) {
			let userHours = 0
			for (const action of Object.values(userActions[user.id] ?? {})) {
				userHours += action.totalTimeSpent ?? 0
				totalVisits += action.count ?? 0
			}
			if (userHours > 0) usersWithTime++
			totalHours += userHours
		}

		return {
			usersShown: filteredUsers.length,
			usersWithTime,
			totalVisits,
			totalHours,
			avgHoursPerUser: usersWithTime > 0 ? totalHours / usersWithTime : 0,
		}
	}, [filteredUsers, userActions])

	const companyEngagementRows = useMemo(() => {
		const stats = new Map<
			string,
			{ activeUsers: number; totalHours: number; totalVisits: number }
		>()

		for (const user of filteredUsers) {
			const actions = Object.values(userActions[user.id] ?? {})
			if (actions.length === 0) continue
			const current = stats.get(user.companyName) ?? {
				activeUsers: 0,
				totalHours: 0,
				totalVisits: 0,
			}
			stats.set(user.companyName, {
				activeUsers: current.activeUsers + 1,
				totalHours:
					current.totalHours +
					actions.reduce((sum, action) => sum + (action.totalTimeSpent ?? 0), 0),
				totalVisits:
					current.totalVisits + actions.reduce((sum, action) => sum + action.count, 0),
			})
		}

		return [...stats.entries()]
			.map(([company, s]) => ({
				company,
				...s,
				avgHoursPerUser: s.activeUsers > 0 ? s.totalHours / s.activeUsers : 0,
			}))
			.sort((a, b) => {
				const userDiff = b.activeUsers - a.activeUsers
				return userDiff !== 0 ? userDiff : b.totalHours - a.totalHours
			})
			.slice(0, 12)
	}, [filteredUsers, userActions])

	const companyEngagementChartData = useMemo<ChartData | null>(() => {
		if (companyEngagementRows.length === 0) return null
		return {
			labels: companyEngagementRows.map((row) => row.company),
			datasets: [
				{
					label: "Active users",
					data: companyEngagementRows.map((row) => row.activeUsers),
					backgroundColor: "rgba(37, 99, 235, 0.65)",
					borderColor: "rgba(37, 99, 235, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [companyEngagementRows])

	const moduleEngagementRows = useMemo(() => {
		const stats = new Map<
			string,
			{ users: Set<number>; totalHours: number; totalVisits: number; lastTimestamp: Date | null }
		>()

		for (const user of filteredUsers) {
			for (const [actionKey, action] of Object.entries(userActions[user.id] ?? {})) {
				const module = moduleNameFromActionKey(actionKey)
				if (module == null) continue
				const current = stats.get(module) ?? {
					lastTimestamp: null,
					users: new Set<number>(),
					totalHours: 0,
					totalVisits: 0,
				}
				current.users.add(user.id)
				current.totalHours += action.totalTimeSpent ?? 0
				current.totalVisits += action.count ?? 0
				if (current.lastTimestamp == null || action.lastTimestamp > current.lastTimestamp) {
					current.lastTimestamp = action.lastTimestamp
				}
				stats.set(module, current)
			}
		}

		return [...stats.entries()]
			.map(([module, s]) => ({
				module,
				activeUsers: s.users.size,
				lastTimestamp: s.lastTimestamp,
				totalHours: s.totalHours,
				totalVisits: s.totalVisits,
				avgMinutesPerVisit: s.totalVisits > 0 ? (s.totalHours * 60) / s.totalVisits : 0,
			}))
			.sort((a, b) => {
				const userDiff = b.activeUsers - a.activeUsers
				return userDiff !== 0 ? userDiff : b.totalHours - a.totalHours
			})
	}, [filteredUsers, userActions])

	const moduleReachChartData = useMemo<ChartData | null>(() => {
		if (moduleEngagementRows.length === 0) return null
		const rows = moduleEngagementRows.slice(0, MODULE_CHART_TOP_N)
		return {
			labels: rows.map((row) => row.module),
			datasets: [
				{
					label: "Active users",
					data: rows.map((row) => row.activeUsers),
					backgroundColor: "rgba(5, 150, 105, 0.65)",
					borderColor: "rgba(5, 150, 105, 1)",
					borderWidth: 1,
				},
				{
					label: "Visits",
					data: rows.map((row) => row.totalVisits),
					backgroundColor: "rgba(14, 165, 233, 0.55)",
					borderColor: "rgba(14, 165, 233, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [moduleEngagementRows])

	const moduleTimeChartData = useMemo<ChartData | null>(() => {
		const rows = [...moduleEngagementRows]
			.filter((row) => row.totalHours > 0)
			.sort((a, b) => b.totalHours - a.totalHours)
			.slice(0, MODULE_CHART_TOP_N)
		if (rows.length === 0) return null
		return {
			labels: rows.map((row) => row.module),
			datasets: [
				{
					label: "Total time (hours)",
					data: rows.map((row) => row.totalHours),
					backgroundColor: "rgba(21, 237, 158, 0.6)",
					borderColor: "rgba(16, 185, 129, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [moduleEngagementRows])

	const moduleVisitsChartData = useMemo<ChartData | null>(() => {
		const rows = [...moduleEngagementRows]
			.sort((a, b) => b.totalVisits - a.totalVisits)
			.slice(0, MODULE_CHART_TOP_N)
		if (rows.length === 0 || rows.every((row) => row.totalVisits === 0)) return null
		return {
			labels: rows.map((row) => row.module),
			datasets: [
				{
					label: "Visits",
					data: rows.map((row) => row.totalVisits),
					backgroundColor: "rgba(153, 102, 255, 0.6)",
					borderColor: "rgba(124, 58, 237, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [moduleEngagementRows])

	const visitModuleOptions = useMemo(
		() =>
			[...moduleEngagementRows]
				.sort((a, b) => a.module.localeCompare(b.module))
				.map((row) => row.module),
		[moduleEngagementRows]
	)

	useEffect(() => {
		if (visitModuleOptions.length === 0) {
			if (selectedVisitModule !== "") setSelectedVisitModule("")
			return
		}
		if (!visitModuleOptions.includes(selectedVisitModule)) {
			setSelectedVisitModule(visitModuleOptions[0] ?? "")
		}
	}, [visitModuleOptions, selectedVisitModule])

	const topModuleUserRows = useMemo(() => {
		if (selectedVisitModule === "") return []
		return filteredUsers
			.map((user) => {
				let visits = 0
				let totalHours = 0
				let lastTimestamp: Date | null = null

				for (const [actionKey, action] of Object.entries(userActions[user.id] ?? {})) {
					const module = moduleNameFromActionKey(actionKey)
					if (module !== selectedVisitModule) continue
					visits += action.count
					totalHours += action.totalTimeSpent ?? 0
					if (lastTimestamp == null || action.lastTimestamp > lastTimestamp) {
						lastTimestamp = action.lastTimestamp
					}
				}

				return { user, visits, totalHours, lastTimestamp }
			})
			.filter((row) => row.visits > 0)
			.sort((a, b) => {
				const visitDiff = b.visits - a.visits
				return visitDiff !== 0 ? visitDiff : b.totalHours - a.totalHours
			})
			.slice(0, 20)
	}, [filteredUsers, selectedVisitModule, userActions])

	const topModuleUsersChartData = useMemo<ChartData | null>(() => {
		if (topModuleUserRows.length === 0) return null
		return {
			labels: topModuleUserRows.map((row) => row.user.name),
			datasets: [
				{
					label: "Visits",
					data: topModuleUserRows.map((row) => row.visits),
					backgroundColor: "rgba(124, 58, 237, 0.65)",
					borderColor: "rgba(124, 58, 237, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [topModuleUserRows])

	const userTimeSpentBarChartModel = useMemo(() => {
		const withTime = filteredUsers
			.map((user) => ({
				user,
				total: Object.values(userActions[user.id] ?? {}).reduce(
					(t, action) => t + (action.totalTimeSpent ?? 0),
					0
				),
			}))
			.filter(({ total }) => total > 0)
			.sort((a, b) => b.total - a.total)

		const limit = userTimeChartTopN === "all" ? withTime.length : userTimeChartTopN
		const top = withTime.slice(0, limit)
		const rest = withTime.slice(limit)
		const rows: { label: string; hours: number; user: User | null }[] = top.map(
			({ user, total }) => ({ label: user.name, hours: total, user })
		)
		if (rest.length > 0) {
			const othersHours = rest.reduce((s, r) => s + r.total, 0)
			rows.push({
				label: `Others (${rest.length} users)`,
				hours: othersHours,
				user: null,
			})
		}
		return {
			rows,
			usersWithTimeCount: withTime.length,
			namedBarCount: top.length,
			topNChoice: userTimeChartTopN,
		}
	}, [filteredUsers, userActions, userTimeChartTopN])

	const sortedUserActions = useMemo(() => {
		if (selectedUserActions == null) return []

		return Object.entries(selectedUserActions)
			.filter(([actionKey]) => moduleNameFromActionKey(actionKey) != null)
			.sort((a, b) => {
				const [keyA, dataA] = a
				const [keyB, dataB] = b

				if (sortConfig != null) {
					const { key, direction } = sortConfig
					const order = direction === "asc" ? 1 : -1

					if (key === "action") {
						return keyA.localeCompare(keyB) * order
					} else if (key === "component") {
						const componentA = keyA.split("_")[1] ?? ""
						const componentB = keyB.split("_")[1] ?? ""
						return componentA.localeCompare(componentB) * order
					} else if (key === "count") {
						return ((dataA?.count ?? 0) - (dataB?.count ?? 0)) * order
					} else if (key === "lastTimestamp") {
						const timeA = dataA?.lastTimestamp.getTime() ?? 0
						const timeB = dataB?.lastTimestamp.getTime() ?? 0
						return (timeA - timeB) * order
					} else if (key === "totalTimeSpent") {
						return ((dataA?.totalTimeSpent ?? 0) - (dataB?.totalTimeSpent ?? 0)) * order
					}
				}

				return 0 // Default return if no sort config matches
			})
	}, [selectedUserActions, sortConfig])

	const selectedUserModuleRows = useMemo(() => {
		if (selectedUserActions == null) return []

		const stats = new Map<
			string,
			{ totalHours: number; totalVisits: number; lastTimestamp: Date | null }
		>()

		for (const [actionKey, action] of Object.entries(selectedUserActions)) {
			const module = moduleNameFromActionKey(actionKey)
			if (module == null) continue
			const current = stats.get(module) ?? {
				lastTimestamp: null,
				totalHours: 0,
				totalVisits: 0,
			}
			current.totalHours += action.totalTimeSpent ?? 0
			current.totalVisits += action.count ?? 0
			if (current.lastTimestamp == null || action.lastTimestamp > current.lastTimestamp) {
				current.lastTimestamp = action.lastTimestamp
			}
			stats.set(module, current)
		}

		return [...stats.entries()]
			.map(([module, s]) => ({
				module,
				...s,
				avgMinutesPerVisit: s.totalVisits > 0 ? (s.totalHours * 60) / s.totalVisits : 0,
			}))
			.sort((a, b) => {
				const timeDiff = b.totalHours - a.totalHours
				return timeDiff !== 0 ? timeDiff : b.totalVisits - a.totalVisits
			})
	}, [selectedUserActions])

	const selectedUserTimeChartData = useMemo<ChartData | null>(() => {
		const rows = selectedUserModuleRows.filter((row) => row.totalHours > 0)
		if (rows.length === 0) return null
		return {
			labels: rows.map((row) => row.module),
			datasets: [
				{
					label: "Total time (hours)",
					data: rows.map((row) => row.totalHours),
					backgroundColor: "rgba(21, 237, 158, 0.6)",
					borderColor: "rgba(16, 185, 129, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [selectedUserModuleRows])

	const selectedUserVisitsChartData = useMemo<ChartData | null>(() => {
		if (selectedUserModuleRows.length === 0) return null
		const rows = [...selectedUserModuleRows].sort((a, b) => b.totalVisits - a.totalVisits)
		return {
			labels: rows.map((row) => row.module),
			datasets: [
				{
					label: "Visits",
					data: rows.map((row) => row.totalVisits),
					backgroundColor: "rgba(153, 102, 255, 0.6)",
					borderColor: "rgba(124, 58, 237, 1)",
					borderWidth: 1,
				},
			],
		}
	}, [selectedUserModuleRows])

	const browserStats = useMemo(
		() => aggregateBrowserStats(filteredUsers, userActions),
		[filteredUsers, userActions]
	)

	const renderUserTimeSpentChart = () => {
		const { rows, usersWithTimeCount, namedBarCount, topNChoice } = userTimeSpentBarChartModel
		if (rows.length === 0) {
			return (
				<div className="my-8 text-center">
					<h3 className={utChartTitle}>Total Time Spent by Users</h3>
					<p className="text-sm text-slate-600">
						No logged time for users in the current filters.
					</p>
				</div>
			)
		}

		const chartLabels = rows.map((r) => r.label)
		const totalTimeData = rows.map((r) => r.hours)
		const barPx = 28
		const chartHeight = Math.max(rows.length * barPx + 120, 200)

		const chartData = {
			labels: chartLabels,
			datasets: [
				{
					label: "Total Time Spent (hours)",
					data: totalTimeData,
					backgroundColor: rows.map((r) =>
						r.user == null ? "rgba(120, 120, 120, 0.45)" : "rgba(75, 192, 192, 0.6)"
					),
					borderColor: rows.map((r) =>
						r.user == null ? "rgba(90, 90, 90, 0.9)" : "rgba(75, 192, 192, 1)"
					),
					borderWidth: 1,
				},
			],
		}
		const handleBarClick = (event: ChartEvent, elements: ActiveElement[], chart: ChartJS) => {
			if (elements.length > 0 && elements[0]?.index != null) {
				const index = elements[0].index
				const row = rows[index]
				if (row?.user != null) {
					handleUserSelect(row.user)
				}
			}
		}

		const hasOthers = rows.some((r) => r.user == null)
		const caption =
			topNChoice === "all"
				? `${usersWithTimeCount} user${usersWithTimeCount !== 1 ? "s" : ""} with logged time (all shown), ranked by hours.`
				: hasOthers
					? `Top ${namedBarCount} users by hours, plus one bar for everyone else (${usersWithTimeCount} with logged time in this view). Use the table for the full list.`
					: `${usersWithTimeCount} user${usersWithTimeCount !== 1 ? "s" : ""} with logged time, ranked by hours.`

		return (
			<div className="my-8 space-y-3">
				<h3 className={utChartTitle}>Total Time Spent by Users</h3>
				<div className="flex flex-wrap items-center justify-center gap-2.5">
					<label
						htmlFor="user-time-chart-top-n"
						className="text-sm text-slate-600"
					>
						Chart
					</label>
					<select
						id="user-time-chart-top-n"
						value={topNChoice === "all" ? "all" : String(topNChoice)}
						onChange={(e) => {
							const v = e.target.value
							setUserTimeChartTopN(v === "all" ? "all" : Number(v))
						}}
						className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
					>
						{USER_TIME_CHART_TOP_N_CHOICES.map((n) => (
							<option
								key={n}
								value={n}
							>
								Top {n}
							</option>
						))}
						<option value="all">Everyone with time</option>
					</select>
					<span className="text-xs text-slate-500 sm:text-sm">by total hours</span>
				</div>
				<p className="mx-auto max-w-3xl px-3 text-center text-xs text-slate-600 sm:text-sm">
					{caption}
				</p>
				<div className="max-h-[min(480px,50vh)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
					<div
						className="relative"
						style={{ height: `${chartHeight}px` }}
					>
						<Bar
							data={chartData}
							options={{
								indexAxis: "y",
								responsive: true,
								maintainAspectRatio: false,
								onClick: handleBarClick,
								scales: {
									x: {
										beginAtZero: true,
										title: {
											display: true,
											text: "Time Spent (hours)",
										},
									},
									y: {
										ticks: {
											autoSkip: false,
											maxRotation: 0,
											callback: function (value) {
												return chartLabels[value as number] ?? ""
											},
										},
										title: {
											display: true,
											text: "Users",
										},
									},
								},
								plugins: {
									tooltip: {
										callbacks: {
											label: function (tooltipItem) {
												return `Total Time: ${formatTimeSpent(
													Number(tooltipItem.raw)
												)}`
											},
										},
									},
								},
							}}
						/>
					</div>
				</div>
			</div>
		)
	}

	const renderMainContent = () => {
		return (
			<div className="space-y-6">
				<div className="rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm">
					<div className="mb-8 flex flex-col items-center gap-5">
						<div className="flex flex-wrap justify-center gap-3">
							<input
								type="date"
								value={timeRange.from}
								onChange={(e) => handleDateChange("from", e.target.value)}
								min="2024-09-23"
								max={timeRange.to}
								className={`${utInputDate} cursor-pointer`}
							/>
							<input
								type="date"
								value={timeRange.to}
								onChange={(e) => handleDateChange("to", e.target.value)}
								min={timeRange.from}
								max={new Date().toISOString().split("T")[0]}
								className={`${utInputDate} cursor-pointer`}
							/>
						</div>

						<div className="flex flex-wrap justify-center gap-2">
							{["today", "thisMonth", "last30Days"].map((range) => (
								<button
									key={range}
									type="button"
									onClick={() =>
										handleDateRangeSelect(
											range as "today" | "thisMonth" | "last30Days"
										)
									}
									className={utBtnPreset}
								>
									{range === "today"
										? "Today"
										: range === "thisMonth"
											? "This Month"
											: "Last 30 Days"}
								</button>
							))}
						</div>
					</div>

					{metrics != null && (
						<div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-3 sm:gap-5">
							{[
								{ label: "Avg Daily Active Users", value: metrics.dau },
								{ label: "Active Users in Range", value: metrics.mau },
								{
									label: "DAU/Active Ratio",
									value: `${metrics.ratio.toFixed(1)}%`,
								},
							].map(({ label, value }) => (
								<div
									key={label}
									className="rounded-xl border border-slate-100 bg-slate-50/80 px-5 py-6 shadow-sm"
								>
									<div className="mb-2 text-sm font-medium text-slate-600">
										{label}
									</div>
									<div className="text-3xl font-semibold text-blue-600 tabular-nums">
										{value}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
					{[
						{ label: "Users shown", value: summaryStats.usersShown },
						{ label: "With logged time", value: summaryStats.usersWithTime },
						{ label: "Visits", value: summaryStats.totalVisits },
						{ label: "Total time", value: formatTimeSpent(summaryStats.totalHours) },
						{
							label: "Avg per active user",
							value: formatTimeSpent(summaryStats.avgHoursPerUser),
						},
					].map(({ label, value }) => (
						<div
							key={label}
							className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-center shadow-sm"
						>
							<div className="text-xs font-medium tracking-wide text-slate-500 uppercase">
								{label}
							</div>
							<div className="mt-1 text-lg font-semibold text-slate-800 tabular-nums">
								{value}
							</div>
						</div>
					))}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-2">
					<button
						type="button"
						onClick={handleDownloadCSV}
						className={utBtnPrimary}
					>
						Download Data
					</button>
					<button
						type="button"
						onClick={() => setFilterActive((prev) => !prev)}
						className={filterActive ? utBtnToggleOn : utBtnToggleOff}
					>
						{filterActive ? "Active users only" : "All users"}
					</button>
					<button
						type="button"
						onClick={() => setShowCompanyFilter((prev) => !prev)}
						className={showCompanyFilter ? utBtnToggleOn : utBtnToggleOff}
					>
						{showCompanyFilter ? "Hide companies" : "Filter companies"}
					</button>
					<button
						type="button"
						onClick={() => setShowChartFilter((prev) => !prev)}
						className={showChartFilter ? utBtnToggleOn : utBtnToggleOff}
					>
						{showChartFilter ? "Hide chart options" : "Chart options"}
					</button>
				</div>

				{showCompanyFilter && (
					<div className="mx-auto flex max-w-3xl flex-col rounded-xl border border-slate-200 bg-slate-50/80 p-4">
						<span className={utSectionLabel}>Select companies</span>
						<div className={`${utCheckboxGrid} justify-items-start`}>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="select-all-checkbox"
									checked={selectedCompanies.length === companies.length}
									onChange={handleSelectAllCompanies}
									className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="select-all-checkbox"
									className="cursor-pointer font-semibold text-slate-800"
								>
									Select all
								</label>
							</div>

							{companies.map((company) => (
								<div
									key={company}
									className={utCheckboxRow}
								>
									<input
										type="checkbox"
										id={`company-${company}`}
										checked={selectedCompanies.includes(company)}
										onChange={() => handleCompanySelect(company)}
										className="size-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
									/>
									<label
										htmlFor={`company-${company}`}
										className="cursor-pointer truncate"
										title={company}
									>
										{company}
									</label>
								</div>
							))}
						</div>
					</div>
				)}

				{showChartFilter && (
					<div className="mx-auto flex max-w-3xl flex-col rounded-xl border border-slate-200 bg-slate-50/80 p-4">
						<span className={utSectionLabel}>Charts to display</span>
						<div className={`${utCheckboxGrid} sm:max-w-none`}>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-daily-active-users"
									checked={showDailyActiveUsersChart}
									onChange={() => setShowDailyActiveUsersChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-daily-active-users"
									className="cursor-pointer"
								>
									Daily active users
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-company-engagement"
									checked={showCompanyEngagementChart}
									onChange={() => setShowCompanyEngagementChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-company-engagement"
									className="cursor-pointer"
								>
									Company engagement
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-module-reach"
									checked={showModuleReachChart}
									onChange={() => setShowModuleReachChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-module-reach"
									className="cursor-pointer"
								>
									Module reach
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-module-time"
									checked={showModuleTimeChart}
									onChange={() => setShowModuleTimeChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-module-time"
									className="cursor-pointer"
								>
									Time by module
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-module-visits"
									checked={showModuleVisitsChart}
									onChange={() => setShowModuleVisitsChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-module-visits"
									className="cursor-pointer"
								>
									Visits by module
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-top-module-users"
									checked={showTopModuleUsersChart}
									onChange={() => setShowTopModuleUsersChart((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-top-module-users"
									className="cursor-pointer"
								>
									Top users by module visits
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="chart-total-time-spent-by-users"
									checked={showTotalTimeSpentByUsersChart}
									onChange={() =>
										setShowTotalTimeSpentByUsersChart((prev) => !prev)
									}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="chart-total-time-spent-by-users"
									className="cursor-pointer"
								>
									Total time spent by users
								</label>
							</div>
							<div className={utCheckboxRow}>
								<input
									type="checkbox"
									id="toggle-ascii-vessel"
									checked={showAsciiVessel}
									onChange={() => setShowAsciiVessel((prev) => !prev)}
									className="size-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
								/>
								<label
									htmlFor="toggle-ascii-vessel"
									className="cursor-pointer"
								>
									Show Dipai vessel
								</label>
							</div>
						</div>
					</div>
				)}

				{showAsciiVessel && (
					<div className="overflow-x-auto rounded-xl border border-amber-200/60 bg-amber-50/30 p-4">
						<AsciiVessel />
					</div>
				)}

				{showDailyActiveUsersChart && dailyActiveUsersChartData != null && (
					<div className="space-y-3">
						<h3 className={`mt-2 ${utChartTitle}`}>Daily active users</h3>
						<div className={utChartPanel}>
							<Line
								data={dailyActiveUsersChartData}
								plugins={[weekendShadingPlugin]}
								options={{
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										x: {
											ticks: {
												autoSkip: true,
												maxRotation: 0,
												maxTicksLimit: 12,
											},
										},
										y: {
											beginAtZero: true,
											title: { display: true, text: "Active users" },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												label: (tooltipItem) =>
													`Active users: ${Number(tooltipItem.raw)}`,
											},
										},
									},
								}}
							/>
						</div>
					</div>
				)}

				{showCompanyEngagementChart && companyEngagementChartData != null && (
					<div className="space-y-3">
						<h3 className={`mt-2 ${utChartTitle}`}>Company engagement</h3>
						<div className={utChartPanel}>
							<Bar
								data={companyEngagementChartData}
								options={{
									indexAxis: "y",
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										x: {
											beginAtZero: true,
											title: { display: true, text: "Active users" },
										},
										y: {
											ticks: { autoSkip: false },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												afterLabel: (tooltipItem) => {
													const row =
														companyEngagementRows[tooltipItem.dataIndex]
													if (row == null) return ""
													return [
														`Visits: ${row.totalVisits}`,
														`Total time: ${formatTimeSpent(row.totalHours)}`,
														`Avg/user: ${formatTimeSpent(row.avgHoursPerUser)}`,
													]
												},
											},
										},
									},
								}}
							/>
						</div>
					</div>
				)}

				{showModuleReachChart && moduleReachChartData != null && (
					<div className="space-y-3">
						<h3 className={`mt-2 ${utChartTitle}`}>Module reach</h3>
						<div className={utChartPanel}>
							<Bar
								data={moduleReachChartData}
								options={{
									indexAxis: "y",
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										x: {
											beginAtZero: true,
											title: { display: true, text: "Count" },
										},
										y: {
											ticks: { autoSkip: false },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												afterBody: (tooltipItems) => {
													const row =
														moduleEngagementRows[
															tooltipItems[0]?.dataIndex ?? -1
														]
													if (row == null) return []
													return [
														`Total time: ${formatTimeSpent(row.totalHours)}`,
														`Avg/visit: ${row.avgMinutesPerVisit.toFixed(1)} minutes`,
													]
												},
											},
										},
									},
								}}
							/>
						</div>
						<div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
							<table className="w-full table-fixed border-collapse">
								<colgroup>
									<col className="w-3/10" />
									<col className="w-3/20" />
									<col className="w-3/20" />
									<col className="w-4/25" />
									<col className="w-7/50" />
									<col className="w-1/10" />
								</colgroup>
								<thead className="bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
									<tr>
										<th className={thCls}>Module</th>
										<th className={thCls}>Users</th>
										<th className={thCls}>Visits</th>
										<th className={thCls}>Total time</th>
										<th className={thCls}>Avg / visit</th>
										<th className={thCls}>Last</th>
									</tr>
								</thead>
								<tbody>
									{moduleEngagementRows.map((row) => (
										<tr
											key={row.module}
											className="transition-colors hover:bg-slate-50"
										>
											<td
												className={tdCls}
												title={row.module}
											>
												{row.module}
											</td>
											<td className={tdCls}>{row.activeUsers}</td>
											<td className={tdCls}>{row.totalVisits}</td>
											<td className={tdCls}>
												{formatTimeSpent(row.totalHours)}
											</td>
											<td className={tdCls}>
												{row.avgMinutesPerVisit.toFixed(1)} minutes
											</td>
											<td className={tdCls}>
												{row.lastTimestamp == null
													? "N/A"
													: timeAgo(row.lastTimestamp)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{showModuleTimeChart && moduleTimeChartData != null && (
					<div className="space-y-3">
						<h3 className={`mt-2 ${utChartTitle}`}>Time by module</h3>
						<div className={utChartPanel}>
							<Bar
								data={moduleTimeChartData}
								options={{
									indexAxis: "y",
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										x: {
											beginAtZero: true,
											title: { display: true, text: "Hours" },
										},
										y: {
											ticks: { autoSkip: false },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												label: (tooltipItem) =>
													`Time: ${formatTimeSpent(Number(tooltipItem.raw))}`,
												afterLabel: (tooltipItem) => {
													const row = moduleEngagementRows.find(
														(r) => r.module === tooltipItem.label
													)
													if (row == null) return ""
													return [
														`Users: ${row.activeUsers}`,
														`Visits: ${row.totalVisits}`,
													]
												},
											},
										},
									},
								}}
							/>
						</div>
					</div>
				)}

				{showModuleVisitsChart && moduleVisitsChartData != null && (
					<div className="space-y-3">
						<h3 className={`mt-2 ${utChartTitle}`}>Visits by module</h3>
						<div className={utChartPanel}>
							<Bar
								data={moduleVisitsChartData}
								options={{
									indexAxis: "y",
									responsive: true,
									maintainAspectRatio: false,
									scales: {
										x: {
											beginAtZero: true,
											title: { display: true, text: "Visits" },
										},
										y: {
											ticks: { autoSkip: false },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												afterLabel: (tooltipItem) => {
													const row = moduleEngagementRows.find(
														(r) => r.module === tooltipItem.label
													)
													if (row == null) return ""
													return [
														`Users: ${row.activeUsers}`,
														`Total time: ${formatTimeSpent(row.totalHours)}`,
													]
												},
											},
										},
									},
								}}
							/>
						</div>
					</div>
				)}

				{showTopModuleUsersChart && topModuleUsersChartData != null && (
					<div className="space-y-3">
						<div className="flex flex-wrap items-center justify-center gap-2.5">
							<h3 className={`m-0 ${utChartTitle}`}>Top users by module visits</h3>
							<select
								value={selectedVisitModule}
								onChange={(e) => setSelectedVisitModule(e.target.value)}
								className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
							>
								{visitModuleOptions.map((module) => (
									<option
										key={module}
										value={module}
									>
										{module}
									</option>
								))}
							</select>
						</div>
						<div className={utChartPanel}>
							<Bar
								data={topModuleUsersChartData}
								options={{
									indexAxis: "y",
									responsive: true,
									maintainAspectRatio: false,
									onClick: (event, elements) => {
										const index = elements[0]?.index
										const row =
											index == null ? undefined : topModuleUserRows[index]
										if (row != null) {
											handleUserSelect(row.user)
										}
									},
									scales: {
										x: {
											beginAtZero: true,
											title: { display: true, text: "Visits" },
										},
										y: {
											ticks: { autoSkip: false },
										},
									},
									plugins: {
										tooltip: {
											callbacks: {
												afterLabel: (tooltipItem) => {
													const row =
														topModuleUserRows[tooltipItem.dataIndex]
													if (row == null) return ""
													return [
														`Company: ${row.user.companyName}`,
														`Total time: ${formatTimeSpent(row.totalHours)}`,
														row.lastTimestamp == null
															? ""
															: `Last visit: ${timeAgo(row.lastTimestamp)}`,
													].filter((line) => line !== "")
												},
											},
										},
									},
								}}
							/>
						</div>
						<div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
							<table className="w-full table-fixed border-collapse">
								<colgroup>
									<col className="w-17/50" />
									<col className="w-13/50" />
									<col className="w-13/100" />
									<col className="w-3/20" />
									<col className="w-3/25" />
								</colgroup>
								<thead className="bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
									<tr>
										<th className={thCls}>User</th>
										<th className={thCls}>Company</th>
										<th className={thCls}>Visits</th>
										<th className={thCls}>Time</th>
										<th className={thCls}>Last</th>
									</tr>
								</thead>
								<tbody>
									{topModuleUserRows.map((row) => (
										<tr
											key={row.user.id}
											onClick={() => handleUserSelect(row.user)}
											className="cursor-pointer transition-colors hover:bg-slate-50"
										>
											<td
												className={tdCls}
												title={row.user.name}
											>
												{row.user.name}
											</td>
											<td className={tdCls}>{row.user.companyName}</td>
											<td className={tdCls}>{row.visits}</td>
											<td className={tdCls}>
												{formatTimeSpent(row.totalHours)}
											</td>
											<td className={tdCls}>
												{row.lastTimestamp == null
													? "N/A"
													: timeAgo(row.lastTimestamp)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{/* Total Time Spent by Users */}
				{showTotalTimeSpentByUsersChart && renderUserTimeSpentChart()}

				{browserStats.length > 0 && (
					<div className="rounded-xl border border-slate-200 bg-white shadow-sm">
						<table className="w-full border-collapse">
							<thead className="bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
								<tr>
									<th
										className={thCls}
										colSpan={3}
									>
										Browser breakdown
									</th>
								</tr>
								<tr>
									<th className={thCls}>Browser</th>
									<th className={thCls}>Users</th>
									<th className={thCls}>Total Time</th>
								</tr>
							</thead>
							<tbody>
								{browserStats.map(({ browser, userCount, totalHours }) => (
									<tr
										key={browser}
										className="transition-colors hover:bg-slate-50"
									>
										<td className={tdCls}>
											<BrowserBadge browser={browser} />
										</td>
										<td className={tdCls}>{userCount}</td>
										<td className={tdCls}>{formatTimeSpent(totalHours)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				<div className="max-h-[min(560px,60vh)] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
					<table className="w-full table-fixed border-collapse">
						<colgroup>
							<col className="w-3/50" />
							<col className="w-9/50" />
							<col className="w-7/50" />
							<col className="w-1/10" />
							<col className="w-7/25" />
							<col className="w-2/25" />
							<col className="w-4/25" />
						</colgroup>
						<thead className="sticky top-0 z-2 bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
							<tr>
								<th className={thCls}>ID</th>
								<th className={thCls}>Name</th>
								<th className={thCls}>Company</th>
								<th className={thCls}>Browser</th>
								<th className={thCls}>Top modules</th>
								<th className={thCls}>Visits</th>
								<th
									className={thSortCls}
									onClick={() => handleSort("totalTimeSpent")}
								>
									Total Time {getSortIndicator("totalTimeSpent", sortConfig)}
								</th>
							</tr>
						</thead>
						<tbody>
							{sortedUsers.map((user) => {
								const actions = userActions[user.id] ?? {}
								const totalTimeSpent = Object.values(actions).reduce(
									(total, action) => total + (action.totalTimeSpent ?? 0),
									0
								)
								const totalVisits = totalVisitsForUser(userActions, user.id)
								const topModules = summarizeTopModules(actions)
								return (
									<tr
										key={user.id}
										onClick={() => handleUserSelect(user)}
										className="cursor-pointer transition-colors hover:bg-slate-50"
									>
										<td className={tdCls}>{user.id}</td>
										<td
											className={tdCls}
											title={user.name}
										>
											{user.name}
										</td>
										<td className={tdCls}>{user.companyName}</td>
										<td className={tdCls}>
											{user.userAgent != null && (
												<BrowserBadge
													browser={getBrowser(user.userAgent)}
												/>
											)}
										</td>
										<td
											className={tdCls}
											title={topModules}
										>
											{topModules}
										</td>
										<td className={tdCls}>{totalVisits}</td>
										<td className={tdCls}>{formatTimeSpent(totalTimeSpent)}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	const renderUserDetails = () => {
		return (
			<div className="space-y-6">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<button
						type="button"
						onClick={() => setSelectedUser(null)}
						className={utBtnToggleOff}
					>
						Back to users
					</button>
					<button
						type="button"
						onClick={() => handleUserSelect(selectedUser!)}
						className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
					>
						Refresh
					</button>
				</div>

				<h2 className="text-center text-xl font-bold tracking-tight text-slate-800 sm:text-2xl">
					{selectedUser!.name}{" "}
					<span className="font-semibold text-slate-600">
						({selectedUser!.companyName})
					</span>
				</h2>

				<h3 className={utChartTitle}>Time spent in modules</h3>
				<div className={utChartPanel}>
					{selectedUserTimeChartData != null && (
						<Bar
							data={selectedUserTimeChartData}
							options={{
								indexAxis: "y",
								responsive: true,
								maintainAspectRatio: false,
								scales: {
									x: {
										beginAtZero: true,
										title: {
											display: true,
											text: "Hours",
										},
									},
									y: {
										ticks: { autoSkip: false },
										title: {
											display: true,
											text: "Modules",
										},
									},
								},
								plugins: {
									tooltip: {
										callbacks: {
											label: (tooltipItem) =>
												`Time: ${formatTimeSpent(Number(tooltipItem.raw))}`,
											afterLabel: (tooltipItem) => {
												const row = selectedUserModuleRows.find(
													(r) => r.module === tooltipItem.label
												)
												if (row == null) return ""
												return [
													`Visits: ${row.totalVisits}`,
													`Avg/visit: ${row.avgMinutesPerVisit.toFixed(1)} minutes`,
												]
											},
										},
									},
								},
							}}
						/>
					)}
				</div>

				<h3 className={`mt-2 ${utChartTitle}`}>Visits per module</h3>
				<div className={utChartPanel}>
					{selectedUserVisitsChartData != null && (
						<Bar
							data={selectedUserVisitsChartData}
							options={{
								indexAxis: "y",
								responsive: true,
								maintainAspectRatio: false,
								scales: {
									x: {
										beginAtZero: true,
										title: {
											display: true,
											text: "Number of Visits",
										},
									},
									y: {
										ticks: { autoSkip: false },
										title: {
											display: true,
											text: "Modules",
										},
									},
								},
								plugins: {
									tooltip: {
										callbacks: {
											afterLabel: (tooltipItem) => {
												const row = selectedUserModuleRows.find(
													(r) => r.module === tooltipItem.label
												)
												if (row == null) return ""
												return [
													`Total time: ${formatTimeSpent(row.totalHours)}`,
													`Avg/visit: ${row.avgMinutesPerVisit.toFixed(1)} minutes`,
												]
											},
										},
									},
								},
							}}
						/>
					)}
				</div>

				<div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
					<table className="w-full table-fixed border-collapse">
						<colgroup>
							<col className="w-17/50" />
							<col className="w-4/25" />
							<col className="w-9/50" />
							<col className="w-9/50" />
							<col className="w-7/50" />
						</colgroup>
						<thead className="bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
							<tr>
								<th className={thCls}>Module</th>
								<th className={thCls}>Visits</th>
								<th className={thCls}>Total time</th>
								<th className={thCls}>Avg / visit</th>
								<th className={thCls}>Last</th>
							</tr>
						</thead>
						<tbody>
							{selectedUserModuleRows.map((row) => (
								<tr
									key={row.module}
									className="transition-colors hover:bg-slate-50"
								>
									<td
										className={tdCls}
										title={row.module}
									>
										{row.module}
									</td>
									<td className={tdCls}>{row.totalVisits}</td>
									<td className={tdCls}>{formatTimeSpent(row.totalHours)}</td>
									<td className={tdCls}>
										{row.avgMinutesPerVisit.toFixed(1)} minutes
									</td>
									<td className={tdCls}>
										{row.lastTimestamp == null
											? "N/A"
											: timeAgo(row.lastTimestamp)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
					<table className="w-full table-fixed border-collapse">
						<thead className="sticky top-0 z-2 bg-white shadow-[0_1px_0_0_rgb(226_232_240)]">
							<tr>
								<th
									className={thSortCls}
									onClick={() => handleSort("action")}
								>
									Module{getSortIndicator("action", sortConfig)}
								</th>
								<th
									className={thSortCls}
									onClick={() => handleSort("count")}
								>
									Visits{getSortIndicator("count", sortConfig)}
								</th>
								<th
									className={thSortCls}
									onClick={() => handleSort("lastTimestamp")}
								>
									Last Visit{getSortIndicator("lastTimestamp", sortConfig)}
								</th>
								<th
									className={thSortCls}
									onClick={() => handleSort("totalTimeSpent")}
								>
									Total Time Spent{getSortIndicator("totalTimeSpent", sortConfig)}
								</th>
							</tr>
						</thead>
						<tbody>
							{sortedUserActions.map(([action, data]) => {
								const date = data.lastTimestamp
								return (
									<tr
										key={action}
										className="transition-colors hover:bg-slate-50"
									>
										<td className={tdCls}>{action}</td>
										<td className={tdCls}>{data.count}</td>
										<td className={tdCls}>{timeAgo(date)}</td>
										<td className={tdCls}>
											{data.totalTimeSpent != null &&
											!isNaN(data.totalTimeSpent)
												? formatTimeSpent(data.totalTimeSpent)
												: "N/A"}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	return (
		<div className="mx-auto max-w-6xl space-y-6 px-4 py-5 sm:px-6">
			{selectedUser != null ? renderUserDetails() : renderMainContent()}
		</div>
	)
}
